import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { CX_LOGIN_SUCCESS } from '@cx-web/feature-auth/events';

import {
  type AuthModalStore,
  closeAuthModal,
  createAuthModalStore,
  getActiveAuthModalStore,
  notifyAuthLogin,
  type OpenAtViewOptions,
  openAuthModalAtView,
  setActiveAuthModalStore,
  type View,
  VIEW_FORGOT_PASSWORD,
  VIEW_LOGIN,
  VIEW_SIGNUP,
} from './store/auth-modal-store';

// Re-exported so existing consumers (and SignupForm/Authentication) keep importing the view contract
// from here; the source of truth now lives in the framework-agnostic store.
export {
  type OpenAtViewOptions,
  type View,
  VIEW_FORGOT_PASSWORD,
  VIEW_LOGIN,
  VIEW_SIGNUP,
  VIEW_VERIFY_EMAIL,
  VIEW_VERIFY_PAYMENT,
} from './store/auth-modal-store';

export type AuthModalContextValue = {
  close: () => void;
  isOpen: boolean;
  // Monotonic; bumped on each successful login this session. Lets in-tree consumers drive optimistic
  // post-login UI without listening to legacy `OC:*` events.
  loginNonce: number;
  openAtView: (view: View, options?: OpenAtViewOptions) => void;
  prefillEmail?: string;
  setIsOpen: (isOpen: boolean) => void;
  toggle: () => void;
  view: View;
};

// Null default (not a no-op object) so `useAuthModal` throws when used outside a provider: a missing
// provider must fail loudly, never silently no-op (NGF-6331 acceptance criterion).
export const AuthModalContext = createContext<AuthModalContextValue | null>(null);
AuthModalContext.displayName = 'AuthModalContext';

export const AuthModalProvider = ({ children }: PropsWithChildren) => {
  // One store per provider (created lazily) keeps per-request SSR state isolated exactly as useState
  // did; the provider is now a thin adapter that reads via useSyncExternalStore and writes via the
  // store helpers. Mirrors BetslipProvider (NGF-6116).
  const storeRef = useRef<AuthModalStore | null>(null);
  if (!storeRef.current) {
    storeRef.current = createAuthModalStore();
  }
  const store = storeRef.current;

  // Publish the live store to non-React consumers (Astro islands, the legacy bridge). The client
  // mounts a single provider, so this pointer always references the active auth modal.
  useEffect(() => {
    setActiveAuthModalStore(store);
    // Clear only if we're still the published store, guarding the transient double-mount window where
    // React runs a new provider's effect before the old provider's cleanup (mirrors BetslipProvider).
    return () => {
      if (getActiveAuthModalStore() === store) setActiveAuthModalStore(null);
    };
  }, [store]);

  // Bridge the client-wide CX_LOGIN_SUCCESS event (dispatched by LoginForm/SignupForm on a successful
  // auth) into the store's loginNonce, so useOptimisticLoginState flips the header to logged-in
  // immediately instead of waiting for a reload to refresh server auth state. This replaces the 2018
  // legacy bridge (deleted in EU-5559) that used to translate the OC widget's OC:LoggedIn event into
  // this same store write — without it, notifyAuthLogin is never called and the navbar only updates on
  // refresh (EU-5591).
  useEffect(() => {
    const onLoginSuccess = () => notifyAuthLogin(store);
    window.addEventListener(CX_LOGIN_SUCCESS, onLoginSuccess);
    return () => window.removeEventListener(CX_LOGIN_SUCCESS, onLoginSuccess);
  }, [store]);

  // getState is a stable reference and returns the same state object until the next setState,
  // satisfying useSyncExternalStore's snapshot contract on both client and server.
  const { isOpen, loginNonce, prefillEmail, view } = useSyncExternalStore(
    store.subscribe,
    store.getState,
    store.getState,
  );

  const close = useCallback(() => closeAuthModal(store), [store]);
  const openAtView = useCallback(
    (view: View, options?: OpenAtViewOptions) => openAuthModalAtView(store, view, options),
    [store],
  );
  // Raw open/close that bypasses openAtView. Clear any leftover prefillEmail when opening so a value
  // from a previous session can't silently seed SignupForm's defaultValue; openAtView is still the
  // path that sets a fresh prefill. (Closing leaves prefill alone — close()/openAtView own that.)
  const setIsOpen = useCallback(
    (isOpen: boolean) => store.setState(isOpen ? { isOpen, prefillEmail: undefined } : { isOpen }),
    [store],
  );
  const toggle = useCallback(() => store.setState(state => ({ isOpen: !state.isOpen })), [store]);

  const value = useMemo<AuthModalContextValue>(
    () => ({ close, isOpen, loginNonce, openAtView, prefillEmail, setIsOpen, toggle, view }),
    [close, isOpen, loginNonce, openAtView, prefillEmail, setIsOpen, toggle, view],
  );

  return <AuthModalContext.Provider value={value}>{children}</AuthModalContext.Provider>;
};

export const useAuthModal = () => {
  const context = useContext(AuthModalContext);
  if (!context) {
    throw new Error('useAuthModal must be used within a AuthModalProvider');
  }
  return context;
};

// Query params read by useAuthModalFromQueryParams, e.g. a password-reset email linking straight to
// `?auth_view=forgot-password&oc_prefill_email=user@example.com`.
const AUTH_VIEW_QUERY_PARAM = 'auth_view';
const PREFILL_EMAIL_QUERY_PARAM = 'oc_prefill_email';

// `URLSearchParams.get()` decodes `+` as a literal space (application/x-www-form-urlencoded
// semantics), which corrupts email addresses that contain an unescaped `+` (e.g. Gmail-style
// sub-addressing: `user+tag@example.com`) into `user tag@example.com`. Pre-escaping `+` to `%2B`
// before parsing neutralises that without hand-rolling the parse: URLSearchParams still handles a
// properly percent-encoded `%2B`, correctly scopes each param to its real boundaries (a literal `?`
// or `&` inside another param's value can't spoof it, unlike a regex), and — critically — never
// throws on a malformed percent sequence (`%`, `%zz`, a trailing `%`), unlike `decodeURIComponent`.
// window.location.search is attacker/user-controllable, and this runs in a mount effect, so a throw
// here would unmount whatever root renders the auth-modal driver (no guard against it exists on the
// atlas-astro server the way apps/atlas/server.ts 400s malformed URLs before they reach the client).
const getRawQueryParam = (search: string, key: string): string | undefined =>
  new URLSearchParams(search.replace(/\+/g, '%2B')).get(key) ?? undefined;

// Views a deep link is allowed to open. Deliberately a small allowlist, not "any View": VERIFY_EMAIL/
// VERIFY_PAYMENT are reached from server-issued links with their own token-bearing params this hook
// doesn't parse, and opening them bare would show a broken form.
const DEEP_LINKABLE_VIEWS: readonly View[] = [VIEW_FORGOT_PASSWORD, VIEW_LOGIN, VIEW_SIGNUP];

/**
 * Opens the auth modal at a specific view on mount when the current URL carries a recognised
 * `auth_view` (`forgot-password`, `login` or `signup`), pre-filling the email field from
 * `oc_prefill_email` if present (forgot-password only — the others have no prefillable field this
 * deep link needs to seed).
 * Non-plus only by convention — call this from atlas/atlas-astro's AuthModalDriver, not from
 * product-plus, which has its own OC+ auth entry points and no such deep link.
 *
 * `login`/`signup` exist for hosts with no auth modal wired into their own header at all
 * (myoddschecker): its "Log In"/"Sign Up" buttons navigate to the repub home with
 * `?auth_view=login`/`?auth_view=signup` instead of opening a modal in place, and this hook is what
 * actually opens it once that navigation lands.
 *
 * Reads `window.location.search` directly rather than a router's `useSearchParams`: atlas-astro's
 * AuthModalDriver renders inside a throwaway `createMemoryRouter` (see its comment) whose own location
 * is not the real browser URL, so only the raw window API reads the actual query string in both hosts.
 */
export const useAuthModalFromQueryParams = (): void => {
  const { openAtView } = useAuthModal();

  useEffect(() => {
    const view = getRawQueryParam(window.location.search, AUTH_VIEW_QUERY_PARAM);
    if (!DEEP_LINKABLE_VIEWS.includes(view as View)) return;
    const prefillEmail =
      view === VIEW_FORGOT_PASSWORD ? getRawQueryParam(window.location.search, PREFILL_EMAIL_QUERY_PARAM) : undefined;

    // Strip both params once consumed — otherwise the email address (PII) persists in the address bar,
    // browser history, server access logs and analytics for the lifetime of the tab/session.
    // `URLSearchParams.delete` operates on the param name only, not its value, so this is unaffected
    // by the `+`-as-space decoding issue `getRawQueryParam` exists to work around above.
    const url = new URL(window.location.href);
    url.searchParams.delete(AUTH_VIEW_QUERY_PARAM);
    url.searchParams.delete(PREFILL_EMAIL_QUERY_PARAM);
    window.history.replaceState(window.history.state, '', url);

    openAtView(view as View, { prefillEmail });
    // Run once on mount only: openAtView is stable (useCallback), and this must not re-fire on later
    // re-renders (e.g. after the user closes the modal) just because the query params are still in the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

/**
 * Optimistic logged-in state for headers that drive the OC auth widget (NGF-6331). Flips to
 * logged-in the moment the widget reports success (`loginNonce` bump) so the account menu updates
 * without waiting for a reload to refresh server auth state, and reverts only when the user dismissed
 * the modal without logging in. `isLoggedIn` is the server-resolved auth state (e.g. RR7's
 * `!!myocData?.myOCUser`, atlas-astro's `isLoggedIn` prop); it seeds the initial value and prevents a
 * revert once the server confirms the session.
 *
 * Shared by RR7 atlas (`LayoutComponents`) and atlas-astro (`CxHeaderWrapper`) so the two headers
 * cannot drift; both already consume `useAuthModal` from this subpath.
 */
export const useOptimisticLoginState = (isLoggedIn: boolean): boolean => {
  const { isOpen: authModalOpen, loginNonce } = useAuthModal();
  const [isOptimisticallyLoggedIn, setIsOptimisticallyLoggedIn] = useState(isLoggedIn);
  // Tracks whether login succeeded during this session, distinguishing "user dismissed without
  // logging in" (revert optimistic state) from "modal closed after a successful login" (keep it,
  // since the server auth state is still stale until the next page load).
  const loginSucceededRef = useRef(false);
  const prevLoginNonceRef = useRef(loginNonce);
  const prevAuthModalOpenRef = useRef(authModalOpen);

  useEffect(() => {
    if (loginNonce === prevLoginNonceRef.current) return;
    prevLoginNonceRef.current = loginNonce;
    loginSucceededRef.current = true;
    setIsOptimisticallyLoggedIn(true);
  }, [loginNonce]);

  // Adopt logged-in state when the server confirms the session out-of-band — e.g. an SPA navigation
  // whose loader returns a now-present `myOCUser` without a full reload. `useState(isLoggedIn)` only
  // captures the mount-time value, so without this the header would render logged-out until reload.
  // `setState(true)` is idempotent (React bails when unchanged), so no extra guard is needed.
  useEffect(() => {
    if (isLoggedIn) setIsOptimisticallyLoggedIn(true);
  }, [isLoggedIn]);

  useEffect(() => {
    const wasOpen = prevAuthModalOpenRef.current;
    prevAuthModalOpenRef.current = authModalOpen;
    if (!wasOpen && authModalOpen) {
      loginSucceededRef.current = false;
    }
    if (wasOpen && !authModalOpen && !loginSucceededRef.current && !isLoggedIn) {
      setIsOptimisticallyLoggedIn(false);
    }
  }, [authModalOpen, isLoggedIn]);

  return isOptimisticallyLoggedIn;
};
